import React from "react"
import cx from "classnames"
import { getInitialValue } from "components/Form"
import { RawLink } from "components/Link"
import { Card, CardContent } from "components/Card"
import { useRouter } from "components/Router"
import { Button } from "components/Button"
import { TestimonialCard } from "components/TestimonialCard"
import { useTranslator } from "components/Translator"
import { Anchor } from "components/Icon/Anchor"
import { Dharmachakra } from "components/Icon/Dharmachakra"
import { Sailboat } from "components/Icon/Sailboat"
import { CheckCircle } from "components/Icon/CheckCircle"
import { Text } from "components/Text"
import { Wrapper } from "components/Wrapper"
import { useBreakpoints } from "hooks/useBreakpoints"
import "./style.css"
import snarkdown from "snarkdown"
import { Metrics } from "components/Metrics"
import { Markdown } from "components/Markdown"
import { List } from "components/List"
import { useMemo } from "react"
import cloneDeep from "lodash/cloneDeep"
import { AutoForm } from "components/Form/AutoForm"

export function UserSecurityRegisterPage(props) {
  const [success, setSuccess] = React.useState(false)
  const containerRef = React.useRef(null)

  React.useEffect(() => {
    if (success) {
      window.scrollTo(0, containerRef.current.offsetTop)
    }
  }, [success])

  return (
    <div ref={containerRef}>
      {success ? (
        <Step2 />
      ) : (
        <Step1 {...props} onSuccess={() => setSuccess(true)} />
      )}
    </div>
  )
}

function Step1(props) {
  const { form, metrics, testimonies, onSuccess } = props
  const translator = useTranslator()
  const { lg } = useBreakpoints()
  const Marker = () => <Anchor className="w-4 pt-1 text-secondary-default" />

  const enhancedForm = useMemo(() => enhanceForm(form), [form])

  return (
    <div className="space-y-16 lg:space-y-20">
      <div className="bg-primary-lighter lg:py-20">
        <Wrapper
          className={cx({
            "grid grid-cols-2 gap-14 order-2": lg,
            "flex flex-col py-10 space-y-8": !lg,
          })}
        >
          {!lg ? <RegisterTitle /> : null}
          <div className="lg:order-last">
            <Card>
              <CardContent className="space-y-6">
                <Text
                  variant="headline3"
                  className="text-primary-dark lg:text-center text-left"
                >
                  {translator.trans(
                    "user.security.register.form.title",
                    null,
                    "pages",
                  )}
                </Text>
                <AutoForm
                  schema={enhancedForm}
                  initialValue={{
                    [enhancedForm.title]: getInitialValue(
                      enhancedForm,
                      props.user,
                    ),
                  }}
                  components={{ SubmitButtonWrapper, SubmitButton }}
                  onSuccess={onSuccess}
                />
              </CardContent>
            </Card>
          </div>
          <div className="lg:order-1 space-y-10">
            {lg ? <RegisterTitle /> : null}
            <div className="space-y-8 text-center">
              <RegisterInformations
                icon={<Anchor className="w-8 text-secondary-default" />}
                title={translator.trans(
                  "user.security.register.presentationItems.0.title",
                  null,
                  "pages",
                )}
                content={translator.trans(
                  "user.security.register.presentationItems.0.content",
                  null,
                  "pages",
                )}
              />
              <RegisterInformations
                icon={<Dharmachakra className="w-8 text-secondary-default" />}
                title={translator.trans(
                  "user.security.register.presentationItems.1.title",
                  null,
                  "pages",
                )}
                content={translator.trans(
                  "user.security.register.presentationItems.1.content",
                  null,
                  "pages",
                )}
              />
              <RegisterInformations
                icon={<Sailboat className="w-8 text-secondary-default" />}
                title={translator.trans(
                  "user.security.register.presentationItems.2.title",
                  null,
                  "pages",
                )}
                content={translator.trans(
                  "user.security.register.presentationItems.2.content",
                  null,
                  "pages",
                )}
              />
            </div>
          </div>
        </Wrapper>
      </div>
      <Wrapper className="space-y-6">
        <Text
          variant="headline3"
          className="text-primary-dark text-center lg:text-left"
        >
          {translator.trans(
            "user.security.register.registerBenefits.title",
            null,
            "pages",
          )}
        </Text>
        <div className={cx({ "space-y-6": !lg, "grid grid-cols-2 gap-4": lg })}>
          <List marker={<Marker />}>
            <List.Item>
              {translator.trans(
                "user.security.register.registerBenefits.benefits.0",
                null,
                "pages",
              )}
            </List.Item>
            <List.Item>
              {translator.trans(
                "user.security.register.registerBenefits.benefits.1",
                null,
                "pages",
              )}
            </List.Item>
            <List.Item>
              {translator.trans(
                "user.security.register.registerBenefits.benefits.2",
                null,
                "pages",
              )}
            </List.Item>
            <List.Item>
              <Markdown>
                {translator.trans(
                  "user.security.register.registerBenefits.benefits.3",
                  null,
                  "pages",
                )}
              </Markdown>
            </List.Item>
            <List.Item>
              <Markdown>
                {translator.trans(
                  "user.security.register.registerBenefits.benefits.4",
                  null,
                  "pages",
                )}
              </Markdown>
            </List.Item>
            <List.Item>
              {translator.trans(
                "user.security.register.registerBenefits.benefits.5",
                null,
                "pages",
              )}
            </List.Item>
          </List>
        </div>
      </Wrapper>
      <Metrics metrics={metrics} color="primary" />
      <Wrapper className="space-y-6 pb-20">
        <Text variant="headline3" className="text-primary-dark text-center">
          {translator.trans(
            "user.security.register.testimonies.title",
            null,
            "pages",
          )}
        </Text>
        <div className="flex flex-row overflow-x-scroll space-x-6">
          {testimonies.map((testimonial) => {
            return (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                tag={RawLink}
                href={{ route: "app_user_security_login" }}
                className="grow-0 shrink-0 w-10/12"
              />
            )
          })}
        </div>
      </Wrapper>
    </div>
  )
}

const SubmitButtonWrapper = ({ children }) => {
  return <div className="flex flex-col space-y-4">{children}</div>
}

const SubmitButton = ({ status }) => {
  const translator = useTranslator()

  return (
    <React.Fragment>
      <Button
        type="submit"
        busy={status === "loading"}
        disabled={["success", "loading"].includes(status)}
      >
        {translator.trans(
          "user.security.register.form.submitLabel",
          null,
          "pages",
        )}
      </Button>
      <Button
        href={{ route: "app_user_security_login" }}
        variant="text"
        color="secondary"
      >
        {translator.trans("user.security.register.form.login", null, "pages")}
      </Button>
    </React.Fragment>
  )
}

function Step2() {
  const router = useRouter()
  const translator = useTranslator()

  return (
    <div>
      <div className="bg-primary-lighter py-10 lg:py-20">
        <Wrapper size="small" className="">
          <div className="space-y-4 lg:space-y-10 text-center">
            <div className="space-y-4 flex flex-col items-center">
              <CheckCircle className="w-8 text-primary-default" />
              <Text variant="headline1" className="text-primary-dark">
                {translator.trans(
                  "user.security.register.step2.thanks",
                  null,
                  "pages",
                )}
              </Text>
            </div>
            <Text variant="body1">
              {translator.trans(
                "user.security.register.step2.checkMailbox",
                null,
                "pages",
              )}
            </Text>
          </div>
        </Wrapper>
      </div>
      <Wrapper>
        <div className="space-y-6 lg:space-y-8 py-5 lg:py-10">
          <Text variant="headline3" className="text-primary-dark">
            {translator.trans(
              "user.security.register.step2.mailNotReceivedTitle",
              null,
              "pages",
            )}
          </Text>
          <Text variant="body1">
            {translator.trans(
              "user.security.register.step2.mailNotReceivedProcedure",
              null,
              "pages",
            )}
          </Text>
          <Button
            variant="outline"
            color="secondary"
            href={router.generate("app_user_security_forgotten")}
          >
            {translator.trans(
              "user.security.register.step2.sendFormAgain",
              null,
              "pages",
            )}
          </Button>
        </div>
      </Wrapper>
    </div>
  )
}

const enhanceForm = (form) => {
  const enhancedForm = cloneDeep(form)
  enhancedForm.properties.settings.properties.privacyNewsletters.options.variant =
    "simple"
  return enhancedForm
}

function RegisterInformations(props) {
  const { icon, title, content } = props
  const htmlTranslationContent = snarkdown(content)

  return (
    <div className="flex flex-col space-y-4 items-center justify-center">
      {icon}
      <Text variant="headline3">{title}</Text>
      <Text
        dangerouslySetInnerHTML={{ __html: htmlTranslationContent }}
        variant="body1"
      />
    </div>
  )
}

function RegisterTitle() {
  const translator = useTranslator()
  return (
    <Text
      variant="headline1"
      className="text-primary-dark text-center items-center justify-center"
    >
      {translator.trans("user.security.register.title", null, "pages")}
    </Text>
  )
}
